import React, {useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {GiftShopShareDestinationType} from 'types'
import {
  showModalLimitedAccess,
  showModalReprocessKyc,
  useHistory,
  useShareToSocialMedia,
} from 'utils'
import {Button, Modal, Paragraph} from 'common/components'
import {useSelector} from 'lib/redux'
import convertUnit from 'lib/unit'
import {
  GiftShopTemplateShareProps,
  ShareItemData,
} from './GiftShopTemplateShareModalProps'
import GiftShopTemplateShareItem from './GiftShopTemplateShareItem'

const StyledModal = styled(Modal)`
  display: grid;
  width: 100%;
  max-width: ${convertUnit(335)};
  padding: ${convertUnit(20)};
  gap: ${convertUnit(20)};
`

const StyledButton = styled(Button)`
  width: 100%;
`

const StyledTitle = styled(Paragraph)`
  text-align: center;
`

export default function GiftShopTemplateShareModal({
  targetId,
  subTargetId,
  type,
  caption,
  toggleModal,
}: GiftShopTemplateShareProps) {
  const history = useHistory()
  const {translate} = useTranslation()
  const {status: childStatus} = useSelector('parentalConsentState')
  const {status: similarStatus} = useSelector('similarAccountStatus')
  const {kycStatus} = useSelector('yuserActivationState')
  const stateLoading = useState(false)

  const {shareUrl} = useShareToSocialMedia({
    stateLoading,
    targetId,
    subTargetId,
    type,
    caption,
    toggleModal,
  })

  const shareItems: ShareItemData[] = useMemo(
    () => [
      {icon: 'twitter', label: 'X', destination: 'twitter'},
      {
        icon: 'facebook',
        label: 'Facebook',
        destination: 'facebook',
      },
      {
        icon: 'whatsapp',
        label: 'WhatsApp',
        destination: 'whatsapp',
      },
      {icon: 'link', label: translate('global:copyLink'), destination: 'copy'},
    ],
    [translate],
  )

  const handleFeatureDisabled = useCallback(() => {
    if (childStatus) {
      showModalLimitedAccess()
      return
    }
    if (kycStatus === 'onhold') {
      if (similarStatus === 'CHOOSE_SELF') {
        return showModalReprocessKyc()
      }
      return history.push('giftshop_similar_identity', {})
    }
    if (kycStatus === 'unregistered') {
      history.push('giftshop_robopet_activation_selfie', {})
      return
    }
    history.push('giftshop_robopet', {})
  }, [childStatus, history, kycStatus, similarStatus])

  const handleShare = useCallback(
    (destination: GiftShopShareDestinationType) => {
      if (
        childStatus ||
        !(
          kycStatus === 'verified' ||
          kycStatus === 'verified_check' ||
          kycStatus === 'onhold'
        )
      ) {
        handleFeatureDisabled()
        return
      }
      shareUrl(destination)
    },
    [childStatus, handleFeatureDisabled, kycStatus, shareUrl],
  )

  return (
    <StyledModal visible onBackdropPress={toggleModal}>
      <StyledTitle fontWeight="bold" fontSize="l">
        {translate('giftShop:shareTo')}
      </StyledTitle>
      <div>
        {shareItems.map(({icon, label, destination}, index) => (
          <GiftShopTemplateShareItem
            key={index.toString()}
            icon={icon}
            label={label}
            stateLoading={stateLoading}
            onClick={() => handleShare(destination)}
          />
        ))}
      </div>
      <StyledButton
        label={translate('global:close')}
        color="primary_5"
        backgroundColor="white_3"
        backgroundHoverColor="white_4"
        onClick={toggleModal}
      />
    </StyledModal>
  )
}
